import { memo, useMemo, useRef } from 'react';
import { animated, easings, useTransition } from 'react-spring';

import type { ViewProps } from '@/components/atomic/atoms/layout/view/typings';
import Styled from '@/components/atomic/atoms/utils/portals/dropdown-container/styled';
import { DropdownContainerProps } from '@/components/atomic/atoms/utils/portals/dropdown-container/typings';
import useFocusTrap from '@/hooks/use-focus-trap/use-focus-trap.ts';
import useStore from '@/state';
import getAnimationDirection from '@/state/selectors/navigation/get-animation-direction';
import getAnimationDuration from '@/state/selectors/navigation/get-animation-duration';
import getDropdownId from '@/state/selectors/navigation/get-dropdown-id';
import getOpenState from '@/state/selectors/navigation/get-open-state';
import resetDropdownIdAction from '@/state/selectors/navigation/reset-dropdown-id.ts';
import { BasicComponentProps } from '@/typings/generic';
import { useTheme } from '@emotion/react';
import { usePrevious } from '@multitenancy-spa/hooks/esm/hooks/use-previous';

const AnimatedDropdownContainer = animated(Styled);

const DropdownContainerComponent: React.FC<
	ViewProps & DropdownContainerProps & BasicComponentProps
> = (props) => {
	const duration = useStore(getAnimationDuration);
	const animationDirection = useStore(getAnimationDirection);
	const isOpen = useStore(getOpenState);
	const dropdownId = useStore(getDropdownId);
	const resetDropdownId = useStore(resetDropdownIdAction);
	const wasAlreadyOpen = usePrevious(isOpen);
	const previousDirection = usePrevious(animationDirection);

	const isLanguageSelector = useMemo(
		() => dropdownId === 'language-selector',
		[dropdownId],
	);

	const zIndex = useMemo(
		() => (isLanguageSelector ? 100 : 99),
		[isLanguageSelector],
	);

	const translateY = useMemo(
		() => (isLanguageSelector ? '-20%' : '-100%'),
		[isLanguageSelector],
	);

	const from = useMemo(() => {
		if (animationDirection === 'right') {
			return { translateX: '100%' };
		}
		return {
			translateY,
			opacity: 0,
		};
	}, [animationDirection, translateY]);

	const enter = useMemo(() => {
		if (animationDirection === 'right') {
			return { translateX: '0' };
		}
		return {
			translateY: '0',
			opacity: 1,
			zIndex,
		};
	}, [animationDirection, zIndex]);

	const leave = useMemo(() => {
		if (animationDirection === 'right') {
			return { translateX: '100%' };
		}
		return {
			translateY,
			opacity: 0,
			zIndex,
		};
	}, [animationDirection, translateY, zIndex]);

	const transitions = useTransition(isOpen ? dropdownId : null, {
		keys: isOpen ? dropdownId : null,
		from,
		leave,
		enter,
		immediate:
			isOpen &&
			wasAlreadyOpen &&
			previousDirection === animationDirection,
		onDestroyed: () => {
			if (!isOpen) {
				resetDropdownId();
			}
		},
		config: { duration, easing: easings.easeInOutCubic },
	});

	const { colors } = useTheme();

	const $backgroundColor = useMemo(() => {
		if (dropdownId === 'vehicles') return colors.brandGrayLight;
	}, [colors.brandGrayLight, dropdownId]);

	const ref = useRef<HTMLDivElement>(null);
	useFocusTrap({ ref, enable: isOpen });

	return (
		<>
			{transitions((style, item) =>
				item ? (
					<AnimatedDropdownContainer
						{...props}
						ref={ref}
						style={style}
						$backgroundColor={$backgroundColor}
						hasBreadcrumbs={
							isLanguageSelector ? false : props.hasBreadcrumbs
						}
					/>
				) : null,
			)}
		</>
	);
};

const DropdownContainer = memo(DropdownContainerComponent);

export default DropdownContainer;
